import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'pb.theme.mode';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private media =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;

  private readonly _mode$ = new BehaviorSubject<ThemeMode>('system');
  readonly mode$ = this._mode$.asObservable();

  constructor() {
    // Keep “system” reactive while app is running
    try {
      this.media?.addEventListener?.('change', (e) => {
        if (this._mode$.value === 'system') {
          this.apply('system', e.matches);
        }
      });
    } catch {
      // ignore
    }
  }

  /** Run once on app start (APP_INITIALIZER). */
  init(): void {
    const mode = this.getMode();
    this.apply(mode);
  }

  get mode(): ThemeMode {
    return this._mode$.value;
  }

  setMode(mode: ThemeMode): void {
    this.persist(mode);
    this.apply(mode);
  }

  getMode(): ThemeMode {
    // localStorage (web)
    try {
      const raw = String(localStorage.getItem(STORAGE_KEY) ?? '').trim();
      if (raw === 'system' || raw === 'light' || raw === 'dark') {
        return raw;
      }
    } catch {
      // ignore
    }

    return this._mode$.value;
  }

  private persist(mode: ThemeMode): void {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }

  private apply(mode: ThemeMode, systemDark?: boolean): void {
    if (typeof document === 'undefined') return;

    const prefersDark = systemDark ?? !!this.media?.matches;
    const isDark = mode === 'dark' || (mode === 'system' && prefersDark);

    // IMPORTANT: our tokens are defined under :root and :root.dark
    const html = document.documentElement;
    html.classList.toggle('dark', isDark);
    html.classList.toggle('ion-palette-dark', isDark);
    html.setAttribute('data-theme', mode);

    this._mode$.next(mode);
  }
}
